import * as yup from "yup";
import { isValidCpfOrCnpj, isValidEmail, isValidPhone } from "@/shared/helpers/validation";
import type { FormFieldsConfig, LoginField, RegisterField } from "../types/auth-form-fields.dto";

/**
 * Campos dos formulários de auth como DADOS. Cada campo aponta para uma
 * chave do client__setting (configKey) — a visibilidade e a obrigatoriedade
 * vêm da config do cliente (login_form_fields / register_form_fields).
 *
 * `schema(required)` devolve a validação yup conforme o campo é ou não
 * obrigatório; validações de formato sempre pulam quando o campo está vazio.
 */

export interface AuthFieldOption {
    value: string;
    label: string;
}

export interface AuthFieldDef {
    name: string;
    label: string;
    type?: "text" | "email" | "password" | "checkbox" | "date" | "select";
    /** Opções do `type: "select"` — os `value` são os aceitos pelo back. */
    options?: AuthFieldOption[];
    /**
     * `cpf-cnpj`/`phone`/`date` são máscaras do big2be-ui (prop `mask` do
     * TextInput) — `date` formata "DD/MM/AAAA" digitando, sem depender do
     * `<input type="date">` nativo (cujo segmento de ano aceita mais de 4
     * dígitos no Chrome). `email` não é máscara — só normaliza a digitação
     * (minúsculas, sem espaços).
     */
    mask?: "cpf-cnpj" | "phone" | "email" | "date";
    placeholder?: string;
    autoComplete?: string;
    /** Trava a digitação no input (`maxLength` nativo) além de validar no schema. */
    maxLength?: number;
    /** Ocupa a linha inteira no grid de 2 colunas (default: meia coluna). */
    full?: boolean;
    /** Chave no client__setting; sem configKey = sempre visível/obrigatório. */
    configKey?: LoginField | RegisterField;
    schema: (required: boolean) => yup.AnySchema;
}

// Validações de formato que só disparam quando há valor (para o modo opcional)
const validDoc = (v?: string) => !v || isValidCpfOrCnpj(v);
const validEmail = (v?: string) => !v || isValidEmail(v);
const validPhone = (v?: string) => !v || isValidPhone(v);
const minLen = (n: number) => (v?: string) => !v || v.length >= n;
/** Remove espaços nas pontas e colapsa espaços múltiplos no meio em um só. */
const normalizeSpaces = (v: unknown) => (typeof v === "string" ? v.trim().replace(/\s+/g, " ") : v);
const oneOf = (options: AuthFieldOption[]) => (v?: string) =>
    !v || options.some((option) => option.value === v);
/** Data mínima aceita — antes disso é ano digitado errado, não idade real. */
const MIN_BIRTH_YEAR = 1900;

/** Converte "DD/MM/AAAA" (valor da máscara `date`) num `Date` real, ou `null` se inválido/incompleto. */
function parseBrDate(v: string): Date | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
    if (!match) return null;
    const [, day, month, year] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    // `Date` "rola" dias/meses inválidos (ex.: 31/02) para o mês seguinte —
    // conferir os componentes de volta é o jeito de pegar isso.
    if (
        date.getFullYear() !== Number(year) ||
        date.getMonth() !== Number(month) - 1 ||
        date.getDate() !== Number(day)
    ) {
        return null;
    }
    return date;
}

/** Data real, com ano plausível (>= 1900) e não futura. */
const validBirthDate = (v?: string) => {
    if (!v) return true;
    const date = parseBrDate(v);
    if (!date) return false;
    return date.getFullYear() >= MIN_BIRTH_YEAR && date <= new Date();
};

/** "DD/MM/AAAA" (valor da máscara) → "AAAA-MM-DD" (formato exigido pelo back, `@IsDateString`). */
export function birthDateToIso(v?: string): string | undefined {
    if (!v) return undefined;
    const date = parseBrDate(v);
    if (!date) return undefined;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Valores aceitos pelo back (CustomerRegistrationDto: @IsIn) — não invente novos. */
const GENDER_OPTIONS: AuthFieldOption[] = [
    { value: "masculino", label: "Masculino" },
    { value: "feminino", label: "Feminino" },
    { value: "outro", label: "Outro" },
    { value: "prefiro_nao_informar", label: "Prefiro não informar" },
];

const MARITAL_STATUS_OPTIONS: AuthFieldOption[] = [
    { value: "solteiro", label: "Solteiro(a)" },
    { value: "casado", label: "Casado(a)" },
    { value: "divorciado", label: "Divorciado(a)" },
    { value: "viuvo", label: "Viúvo(a)" },
    { value: "uniao_estavel", label: "União estável" },
];

/** Sim/Não em vez de checkbox: o back exige uma resposta explícita, e um
 *  checkbox desmarcado não distingue "não tem filhos" de "não respondeu". */
export const HAS_CHILDREN_OPTIONS: AuthFieldOption[] = [
    { value: "sim", label: "Sim" },
    { value: "nao", label: "Não" },
];

const withRequired = (base: yup.StringSchema, required: boolean, message: string) =>
    required ? base.required(message) : base;

const cpfCnpjField: AuthFieldDef = {
    name: "cpfCnpj",
    label: "CPF/CNPJ",
    mask: "cpf-cnpj",
    placeholder: "Digite seu CPF ou CNPJ",
    autoComplete: "username",
    configKey: "document",
    schema: (required) =>
        withRequired(
            yup.string().test("document", "CPF/CNPJ inválido", validDoc),
            required,
            "Informe seu CPF/CNPJ"
        ),
};

const emailField: AuthFieldDef = {
    name: "email",
    label: "E-mail",
    type: "email",
    mask: "email",
    placeholder: "Digite seu e-mail",
    autoComplete: "email",
    configKey: "email",
    schema: (required) =>
        withRequired(
            yup.string().test("email", "E-mail inválido", validEmail),
            required,
            "Informe seu e-mail"
        ),
};

export interface LoginFormValues {
    cpfCnpj?: string;
    email?: string;
    phone?: string;
    /** Campo único, usado quando a loja habilita mais de um identificador. */
    identifier?: string;
    password: string;
}

export const LOGIN_FIELDS: AuthFieldDef[] = [
    { ...cpfCnpjField, full: true },
    { ...emailField, full: true },
    {
        name: "phone",
        label: "Telefone",
        mask: "phone",
        placeholder: "(00) 00000-0000",
        autoComplete: "tel",
        full: true,
        configKey: "phone",
        schema: (required) =>
            withRequired(
                yup.string().test("phone", "Telefone inválido", validPhone),
                required,
                "Informe seu telefone"
            ),
    },
    {
        name: "password",
        label: "Senha",
        type: "password",
        placeholder: "Digite sua senha",
        autoComplete: "current-password",
        full: true,
        configKey: "password",
        // Sem mínimo de propósito: aqui o cliente digita a senha que JÁ existe.
        // Contas criadas antes do mínimo de 8 têm senha de 6 caracteres e
        // precisam continuar conseguindo entrar — quem valida é a API.
        schema: (required) => withRequired(yup.string(), required, "Informe sua senha"),
    },
];

export interface RegisterFormValues {
    firstName?: string;
    lastName?: string;
    email?: string;
    cpfCnpj?: string;
    phoneNumber?: string;
    birthDate?: string;
    gender?: string;
    profession?: string;
    maritalStatus?: string;
    hasChildren?: string;
    password?: string;
    confirmPassword?: string;
    communicationAccept?: boolean;
    acceptTerms?: boolean;
}

export const REGISTER_FIELDS: AuthFieldDef[] = [
    {
        name: "firstName",
        label: "Nome",
        placeholder: "Seu nome",
        autoComplete: "given-name",
        configKey: "fullName",
        maxLength: 255,
        schema: (required) =>
            withRequired(
                yup
                    .string()
                    .transform(normalizeSpaces)
                    .max(255, "O nome deve conter no máximo 255 caracteres.")
                    .test("min", "Mínimo 2 caracteres", minLen(2)),
                required,
                "Informe seu nome"
            ),
    },
    {
        name: "lastName",
        label: "Sobrenome",
        placeholder: "Seu sobrenome",
        autoComplete: "family-name",
        configKey: "fullName",
        maxLength: 255,
        schema: (required) =>
            withRequired(
                yup
                    .string()
                    .transform(normalizeSpaces)
                    .max(255, "O sobrenome deve conter no máximo 255 caracteres.")
                    .test("min", "Mínimo 2 caracteres", minLen(2)),
                required,
                "Informe seu sobrenome"
            ),
    },
    emailField,
    cpfCnpjField,
    {
        name: "phoneNumber",
        label: "Telefone",
        mask: "phone",
        placeholder: "(00) 00000-0000",
        autoComplete: "tel",
        full: true,
        configKey: "phone",
        schema: (required) =>
            withRequired(
                yup.string().test("phone", "Telefone inválido", validPhone),
                required,
                "Informe seu telefone"
            ),
    },
    {
        name: "birthDate",
        label: "Data de nascimento",
        mask: "date",
        placeholder: "dd/mm/aaaa",
        autoComplete: "bday",
        configKey: "birthDate",
        schema: (required) =>
            withRequired(
                yup.string().test("birthDate", "Data de nascimento inválida", validBirthDate),
                required,
                "Informe sua data de nascimento"
            ),
    },
    {
        name: "gender",
        label: "Gênero",
        type: "select",
        options: GENDER_OPTIONS,
        placeholder: "Selecione",
        configKey: "gender",
        schema: (required) =>
            withRequired(
                yup.string().test("gender", "Gênero inválido", oneOf(GENDER_OPTIONS)),
                required,
                "Informe seu gênero"
            ),
    },
    {
        name: "profession",
        label: "Profissão",
        placeholder: "Sua profissão",
        autoComplete: "organization-title",
        configKey: "profession",
        maxLength: 100,
        schema: (required) =>
            withRequired(
                yup
                    .string()
                    .max(100, "A profissão deve conter no máximo 100 caracteres.")
                    .test("min", "Mínimo 2 caracteres", minLen(2)),
                required,
                "Informe sua profissão"
            ),
    },
    {
        name: "maritalStatus",
        label: "Estado civil",
        type: "select",
        options: MARITAL_STATUS_OPTIONS,
        placeholder: "Selecione",
        configKey: "maritalStatus",
        schema: (required) =>
            withRequired(
                yup.string().test("maritalStatus", "Estado civil inválido", oneOf(MARITAL_STATUS_OPTIONS)),
                required,
                "Informe seu estado civil"
            ),
    },
    {
        name: "hasChildren",
        label: "Possui filhos",
        type: "select",
        options: HAS_CHILDREN_OPTIONS,
        placeholder: "Selecione",
        configKey: "hasChildren",
        schema: (required) =>
            withRequired(
                yup.string().test("hasChildren", "Valor inválido", oneOf(HAS_CHILDREN_OPTIONS)),
                required,
                "Informe se possui filhos"
            ),
    },
    {
        name: "password",
        label: "Senha",
        type: "password",
        placeholder: "8 a 50 caracteres",
        autoComplete: "new-password",
        configKey: "password",
        maxLength: 50,
        // Mesmo intervalo do back (CustomerRegistrationDto: `@Length(8, 50)`).
        schema: (required) =>
            withRequired(
                yup
                    .string()
                    .max(50, "A senha deve ter no máximo 50 caracteres.")
                    .test("min", "Mínimo 8 caracteres", minLen(8)),
                required,
                "Informe uma senha"
            ),
    },
    {
        name: "confirmPassword",
        label: "Confirmar senha",
        type: "password",
        placeholder: "Repita a senha",
        autoComplete: "new-password",
        maxLength: 50,
        // Segue a senha: só aparece/exige quando "password" está visível/obrigatório
        configKey: "password",
        schema: (required) =>
            withRequired(
                yup.string().oneOf([yup.ref("password")], "As senhas não conferem"),
                required,
                "Confirme sua senha"
            ),
    },
    {
        name: "communicationAccept",
        label: "Aceito receber comunicações e ofertas.",
        type: "checkbox",
        configKey: "acceptCommunications",
        // Mesmo `.test` do aceite de termos (e não `.oneOf`) — e exigir `true`
        // quando obrigatório espelha o back, que recusa `communicationAccept`
        // diferente de `true` nesse caso.
        schema: (required) =>
            required
                ? yup
                      .boolean()
                      .test("accepted", "Aceite as comunicações para continuar", (v) => v === true)
                : yup.boolean().optional(),
    },
    {
        name: "acceptTerms",
        label: "Li e aceito os Termos de Uso e a Política de Privacidade.",
        type: "checkbox",
        configKey: "acceptTerms",
        // `.test` (e não `.oneOf`) porque `oneOf([true])` do yup IGNORA valores
        // vazios (undefined) — um checkbox nunca tocado passaria. O test roda
        // pra qualquer valor e só aceita `true`.
        schema: (required) =>
            required
                ? yup
                      .boolean()
                      .test("accepted", "Aceite os termos para continuar", (v) => v === true)
                : yup.boolean().optional(),
    },
];

type AnyFormConfig = FormFieldsConfig<LoginField> | FormFieldsConfig<RegisterField>;

function fieldSetting(config: AnyFormConfig, key: AuthFieldDef["configKey"]) {
    if (!key) return { visible: true, required: true };
    return config.fields[key as keyof typeof config.fields] ?? { visible: false, required: false };
}

/** Campos do login que identificam o cliente — ele entra com UM deles. */
export const LOGIN_IDENTIFIERS = ["cpfCnpj", "email", "phone"] as const;

const isIdentifier = (def: AuthFieldDef) =>
    (LOGIN_IDENTIFIERS as readonly string[]).includes(def.name);

/** Identificadores habilitados na config, na ordem de LOGIN_FIELDS. */
export function loginIdentifiers(config: FormFieldsConfig<LoginField>): AuthFieldDef[] {
    return visibleFields(LOGIN_FIELDS, config).filter(isIdentifier);
}

/** "CPF/CNPJ ou E-mail" — só com o que a loja habilitou. */
function identifierLabel(identifiers: AuthFieldDef[]): string {
    const labels = identifiers.map((def) => def.label);
    if (labels.length <= 1) return labels[0] ?? "Identificador";
    return `${labels.slice(0, -1).join(", ")} ou ${labels[labels.length - 1]}`;
}

export type ResolvedIdentifier = Pick<LoginFormValues, "cpfCnpj" | "email" | "phone">;

/**
 * Descobre QUAL identificador o cliente digitou no campo único — não existe
 * seletor, ele só digita.
 *
 * Letra ou "@" em qualquer posição ⇒ e-mail (cobre e-mail que começa com
 * dígito, tipo "123@loja.com"). Só dígitos ⇒ documento ou telefone, e o
 * empate real (CPF e celular têm ambos 11 dígitos) é desfeito pelo dígito
 * verificador do CPF, que um telefone praticamente nunca satisfaz — ainda mais
 * porque `isValidPhone` exige o "9" na 3ª casa do celular.
 *
 * Devolve `null` quando nada bate: aí o campo acusa erro em vez de mandar um
 * palpite pro back.
 */
export function resolveLoginIdentifier(
    raw: string | undefined,
    identifiers: AuthFieldDef[]
): ResolvedIdentifier | null {
    const value = (raw ?? "").trim();
    if (!value) return null;

    const has = (name: string) => identifiers.some((def) => def.name === name);
    const pareceTexto = /[a-zA-Z@]/.test(value);

    if (pareceTexto) {
        return has("email") && isValidEmail(value) ? { email: value.toLowerCase() } : null;
    }

    if (has("cpfCnpj") && isValidCpfOrCnpj(value)) return { cpfCnpj: value };
    if (has("phone") && isValidPhone(value)) return { phone: value };
    return null;
}

/**
 * Campo ÚNICO de identificador, usado quando a loja habilita mais de um: o
 * cliente digita e-mail, CPF/CNPJ ou celular no mesmo input, sem escolher nada
 * antes. Sem máscara porque o conteúdo pode ser texto ou número — a formatação
 * seria adivinhação a cada tecla.
 */
export function loginIdentifierField(identifiers: AuthFieldDef[]): AuthFieldDef {
    const label = identifierLabel(identifiers);
    const minusculo = label.toLowerCase();

    return {
        name: "identifier",
        label,
        placeholder: `Digite seu ${minusculo}`,
        autoComplete: "username",
        full: true,
        schema: (required) =>
            withRequired(
                yup
                    .string()
                    .test(
                        "identifier",
                        `Informe um ${minusculo} válido`,
                        (v) => !v || !!resolveLoginIdentifier(v, identifiers)
                    ),
                required,
                `Informe seu ${minusculo}`
            ),
    };
}

/**
 * Campo de identificador do login: o próprio (com máscara e rótulo) quando a
 * loja habilita um só, ou o campo único quando habilita vários.
 */
export function loginIdentifierInput(config: FormFieldsConfig<LoginField>): AuthFieldDef | undefined {
    const identifiers = loginIdentifiers(config);
    return identifiers.length > 1 ? loginIdentifierField(identifiers) : identifiers[0];
}

/**
 * Schema do login. O identificador é sempre obrigatório — não existe login sem
 * ele, independente do `required` da config (que vem falso justamente porque,
 * havendo vários, são alternativas entre si).
 */
export function buildLoginSchema(config: FormFieldsConfig<LoginField>) {
    const entries = visibleFields(LOGIN_FIELDS, config)
        .filter((def) => !isIdentifier(def))
        .map((def) => {
            const { required } = fieldSetting(config, def.configKey);
            return [def.name, def.schema(required)] as const;
        });

    const identifier = loginIdentifierInput(config);
    if (identifier) entries.push([identifier.name, identifier.schema(true)]);

    return yup.object(Object.fromEntries(entries));
}

/** Filtra os campos que devem ser exibidos conforme a config do cliente. */
export function visibleFields(defs: AuthFieldDef[], config: AnyFormConfig): AuthFieldDef[] {
    return defs.filter((def) => fieldSetting(config, def.configKey).visible);
}

/** Monta o schema só com os campos visíveis, respeitando o required da config. */
export function buildSchema(defs: AuthFieldDef[], config: AnyFormConfig) {
    const entries = visibleFields(defs, config).map((def) => {
        const { required } = fieldSetting(config, def.configKey);
        return [def.name, def.schema(required)] as const;
    });

    return yup.object(Object.fromEntries(entries));
}
